'use client'

import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'

type PriceDisplayProps = {
  gross?: number | null
  originalGross?: number | null
  net?: number | null
  taxAmount?: number | null
  discountRate?: number | null
  currencyCode?: string
  locale: string
  className?: string
  discountedClassName?: string
  originalClassName?: string
  badgeClassName?: string
  size?: 'sm' | 'md' | 'lg'
  showTaxBreakdown?: boolean
  priceIncludesTax?: boolean
}

const formatPrice = (amount: number, locale: string, currencyCode?: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currencyCode ?? ''}`
  }
}

const PriceDisplay = ({
  gross,
  originalGross,
  net,
  taxAmount,
  discountRate,
  currencyCode,
  locale,
  className,
  discountedClassName,
  originalClassName,
  badgeClassName,
  size = 'md',
  showTaxBreakdown = false,
  priceIncludesTax
}: PriceDisplayProps) => {
  const t = useTranslations()
  if (gross === null || gross === undefined) {
    return null
  }

  const hasDiscount = discountRate !== null && discountRate !== undefined && discountRate > 0

  const sizeMap = {
    sm: { discounted: 'text-sm font-bold', original: 'text-xs', badge: 'text-[10px] px-1 py-0', tax: 'text-[10px]' },
    md: { discounted: 'text-sm font-bold', original: 'text-xs', badge: 'text-[10px] px-1 py-0', tax: 'text-[10px]' },
    lg: {
      discounted: 'text-2xl font-bold md:text-3xl',
      original: 'text-sm',
      badge: 'text-xs px-1.5 py-0.5',
      tax: 'text-xs'
    }
  }

  const sizes = sizeMap[size]

  const taxLabel =
    showTaxBreakdown && taxAmount !== null && taxAmount !== undefined && net !== null && net !== undefined
      ? priceIncludesTax === false
        ? t('tax_excl', { amount: formatPrice(taxAmount, locale, currencyCode), currency: currencyCode ?? '' })
        : t('tax_incl', { amount: formatPrice(taxAmount, locale, currencyCode) })
      : null

  if (!hasDiscount) {
    return (
      <span className={cn('flex flex-col items-end gap-0.5', className)}>
        <span className={cn(sizes.discounted, 'text-gray-900', discountedClassName)}>
          {formatPrice(gross, locale, currencyCode)}
        </span>
        {taxLabel && <span className={cn(sizes.tax, 'text-muted-foreground')}>{taxLabel}</span>}
      </span>
    )
  }

  return (
    <span className={cn('flex flex-col items-end gap-0.5', className)}>
      <span className={cn(sizes.discounted, 'text-gray-900', discountedClassName)}>
        {formatPrice(gross, locale, currencyCode)}
      </span>

      <span className="flex flex-wrap items-center gap-1.5">
        {originalGross !== null && originalGross !== undefined && (
          <span className={cn('text-muted-foreground line-through', sizes.original, originalClassName)}>
            {formatPrice(originalGross, locale, currencyCode)}
          </span>
        )}
        <span
          className={cn(
            'inline-flex items-center rounded bg-green-100 font-semibold text-green-700',
            sizes.badge,
            badgeClassName
          )}>
          -{discountRate}%
        </span>
      </span>

      {taxLabel && <span className={cn(sizes.tax, 'text-muted-foreground')}>{taxLabel}</span>}
    </span>
  )
}

export default PriceDisplay
